import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Heart, MessageCircle, Share2, Send, ImagePlus, Loader2 } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const s = Math.floor((Date.now() - t) / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24); if (d < 7) return `${d}d`;
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
}

function PostCard({ post }) {
  const [likes, setLikes] = useState(post.likes_count || post.like_count || 0);
  const [liked, setLiked] = useState(false);
  const [comments, setComments] = useState(post.comments || []);
  const [newComment, setNewComment] = useState("");
  const [showComments, setShowComments] = useState(false);

  const share = async () => {
    const url = window.location.href + "#post-" + (post.id || "");
    try {
      if (navigator.share) await navigator.share({ title: "Film Front", text: post.content, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      }
    } catch {}
  };

  const authorName = post.author_name || post.author?.name || "Member";
  const initials = authorName.split(" ").map(s => s[0]).slice(0,2).join("").toUpperCase();

  return (
    <article id={`post-${post.id}`} className="ff-card p-6" data-testid={`community-post-${post.id}`}>
      <header className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-full ff-accent-bg text-black font-semibold flex items-center justify-center flex-shrink-0">
          {post.author_avatar_url
            ? <img src={post.author_avatar_url} className="w-full h-full rounded-full object-cover" alt="" />
            : initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base tracking-tight truncate">{authorName}</div>
          <div className="text-xs text-neutral-500">
            {post.author_headline || "Southern Utah film"} · {timeAgo(post.created_at)}
          </div>
        </div>
      </header>
      <div className="text-neutral-200 text-[15px] leading-relaxed whitespace-pre-wrap">
        {post.content || post.body || post.text}
      </div>
      {post.image_url && (
        <img src={post.image_url} className="mt-4 w-full rounded-lg border border-white/10" alt="" />
      )}
      <div className="mt-5 flex items-center gap-1 border-t border-white/5 pt-3">
        <button
          onClick={() => { setLiked((l) => !l); setLikes((n) => n + (liked ? -1 : 1)); }}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${liked ? "ff-accent-text bg-white/5" : "text-neutral-400 hover:text-white hover:bg-white/5"}`}
          data-testid={`community-like-btn-${post.id}`}
        >
          <Heart className={`w-4 h-4 ${liked ? "fill-current" : ""}`} /> {likes}
        </button>
        <button
          onClick={() => setShowComments((s) => !s)}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm text-neutral-400 hover:text-white hover:bg-white/5 transition-colors"
          data-testid={`community-comment-btn-${post.id}`}
        >
          <MessageCircle className="w-4 h-4" /> {comments.length}
        </button>
        <button
          onClick={share}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm text-neutral-400 hover:text-white hover:bg-white/5 transition-colors"
          data-testid={`community-share-btn-${post.id}`}
        >
          <Share2 className="w-4 h-4" /> Share
        </button>
      </div>
      {showComments && (
        <div className="mt-4 space-y-3">
          {comments.map((c, i) => (
            <div key={c.id || i} className="text-sm">
              <span className="font-semibold text-white">{c.author_name || "Member"}</span>
              <span className="text-neutral-300 ml-2">{c.content}</span>
            </div>
          ))}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!newComment.trim()) return;
              setComments((cs) => [...cs, { id: Date.now(), author_name: "You", content: newComment.trim() }]);
              setNewComment("");
              toast.success("Comment added (visible to you)");
            }}
            className="flex items-center gap-2"
          >
            <input
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment…"
              className="flex-1 bg-black border border-white/10 rounded-full px-4 py-2 text-sm outline-none focus:border-[hsl(var(--ff-accent))]"
              data-testid={`community-comment-input-${post.id}`}
            />
            <button className="ff-btn-primary text-sm px-4 py-2" type="submit">
              <Send className="w-3.5 h-3.5" />
            </button>
          </form>
        </div>
      )}
    </article>
  );
}

export default function Community() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.feed({ limit: 50 });
      setPosts(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Could not load feed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setPosting(true);
    try {
      const post = await api.createPost({ content: content.trim() });
      setPosts((p) => [post, ...p]);
      setContent("");
      toast.success("Posted to the board");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not create post";
      if (String(detail).includes("verify")) {
        toast.error("Verify your email from the mobile app first.");
      } else {
        toast.error(detail);
      }
    } finally {
      setPosting(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-neutral-400 mb-3">Community</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter leading-none font-medium">The board.</h1>
        <p className="text-neutral-400 mt-3 max-w-xl">Casting calls, shoot recaps, questions, and shoutouts — from the whole Film Front community.</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-8">
        <div>
          {/* Composer */}
          <form onSubmit={submit} className="ff-card p-5 mb-6" data-testid="community-composer">
            <div className="flex gap-3">
              <div className="w-10 h-10 rounded-full ff-accent-bg text-black font-semibold flex items-center justify-center flex-shrink-0">
                {(user?.name || "?").split(" ").map(s => s[0]).slice(0,2).join("").toUpperCase()}
              </div>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="What's happening on set?"
                rows={3}
                className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-neutral-600 resize-none"
                data-testid="community-composer-input"
              />
            </div>
            <div className="flex items-center justify-between mt-3">
              <button type="button" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 hover:text-white transition-colors">
                <ImagePlus className="w-4 h-4" /> Add media
              </button>
              <button type="submit" disabled={posting || !content.trim()} className="ff-btn-primary text-sm disabled:opacity-60" data-testid="community-post-btn">
                {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post"}
              </button>
            </div>
          </form>

          {loading ? (
            <div className="text-sm text-neutral-500">Loading feed…</div>
          ) : posts.length === 0 ? (
            <div className="ff-card p-10 text-center" data-testid="community-empty">
              <div className="font-display text-2xl mb-2">The board is quiet.</div>
              <div className="text-sm text-neutral-400">Be the first to share what you're working on.</div>
            </div>
          ) : (
            <div className="space-y-4 ff-stagger">
              {posts.map((p, i) => <PostCard key={p.id || i} post={p} />)}
            </div>
          )}
        </div>

        <aside className="hidden lg:block space-y-4">
          <div className="ff-card p-5">
            <div className="text-xs uppercase tracking-[0.25em] text-neutral-400 mb-3">Community rules</div>
            <ul className="text-sm text-neutral-300 space-y-2 list-disc pl-4">
              <li>Members are working professionals — keep it respectful.</li>
              <li>No spam, no unpaid volunteer asks disguised as jobs.</li>
              <li>Disclose paid promotion.</li>
              <li>Deal Room is networking only. No securities offered.</li>
            </ul>
          </div>
          <div className="ff-card p-5">
            <div className="text-xs uppercase tracking-[0.25em] text-neutral-400 mb-3">On mobile</div>
            <div className="text-sm text-neutral-300">The Film Front mobile app has push notifications for mentions and comments. Grab it on iOS or Android.</div>
          </div>
        </aside>
      </div>
    </div>
  );
}
